export interface SocialPostFormData {
  identityType: 'business' | 'creator' | 'hybrid';
  businessName: string;
  creatorNiche: string;
  creatorPersonality: string;
  contentPillars: string;
  creatorAudienceType: string;
  creatorTone: string;
  contentStyle: string;
  platform: string;
  postType: string;
  goal: string;
  offerTopic: string;
  targetAudience: string;
  targetAudienceType: string;
  brandVoice: string;
  imageStyle: string;
  creativeDirection: string;
  captionLength: 'Short' | 'Medium' | 'Long';
  includeImage: boolean;
  cta: string;
  hashtags: string;
  specialInstructions: string;
  autoMode: boolean;
  referenceImageBase64: string | null;
  referenceImagePrompt: string;
  theme: string;
  eventPromotion: string;
  referenceStyle: string;
}

export type DailyContentQuantity = '1' | '3' | '7';

export interface DailyContentFormData {
  identityType: 'business' | 'creator' | 'hybrid';
  businessName: string;
  creatorNiche: string;
  creatorPersonality: string;
  contentPillars: string;
  creatorAudienceType: string;
  creatorTone: string;
  contentStyle: string;
  platform: string;
  contentPersonality: string;
  audienceFocus: string;
  location: string;
  dailyGoal: string;
  contentThemes: string[];
  avoidRepetition: boolean;
  quantity: DailyContentQuantity;
}

export interface DailyPostItem {
  day: number;
  postType: string;
  contentCategory: string;
  hook: string;
  caption: string;
  cta: string;
  hashtags: string[];
  imageDirection: string;
  shortVideoIdea?: string;
}

export interface DailyContentResult {
  type: 'daily_content';
  quantity: DailyContentQuantity;
  posts: DailyPostItem[];
  platform: string;
  businessName: string;
}

export interface SocialPlatformVariationMap {
  facebook?: string;
  instagram?: string;
  tiktok?: string;
  linkedin?: string;
}

export interface SocialPostResult {
  type: 'social_post';
  caption1: string;
  caption2: string;
  hooks: string[];
  primaryHook: string;
  contentAngle: string;
  recommendedCta: string;
  ctaSuggestions: string[];
  hashtagSuggestions: string[];
  visualDirection: string;
  imageConcept: string;
  shortVideoIdea: string;
  platformVariations: SocialPlatformVariationMap;
  imageUrl: string | null;
  platform: string;
  postType: string;
  businessName: string;
}

export const CONTENT_PERSONALITIES = [
  { id: 'educator', label: 'Educator', icon: 'ri-book-open-line', desc: 'Teach & inform' },
  { id: 'authority', label: 'Authority', icon: 'ri-award-line', desc: 'Expert & credible' },
  { id: 'friendly', label: 'Friendly', icon: 'ri-emotion-happy-line', desc: 'Warm & approachable' },
  { id: 'storyteller', label: 'Storyteller', icon: 'ri-quill-pen-line', desc: 'Narrative & relatable' },
  { id: 'entertainer', label: 'Entertainer', icon: 'ri-star-smile-line', desc: 'Fun & engaging' },
  { id: 'motivator', label: 'Motivator', icon: 'ri-fire-line', desc: 'Inspire & energize' },
] as const;

export const AUDIENCE_FOCUS_OPTIONS = [
  { id: 'new-customers', label: 'New Customers', icon: 'ri-user-add-line' },
  { id: 'existing-customers', label: 'Existing Customers', icon: 'ri-user-heart-line' },
  { id: 'local-community', label: 'Local Community', icon: 'ri-community-line' },
  { id: 'general-audience', label: 'General Audience', icon: 'ri-global-line' },
] as const;

export const DAILY_GOALS = [
  { id: 'engagement', label: 'Engagement', icon: 'ri-chat-smile-2-line', desc: 'Likes, comments, shares' },
  { id: 'brand-awareness', label: 'Brand Awareness', icon: 'ri-eye-line', desc: 'Reach & visibility' },
  { id: 'trust-building', label: 'Trust Building', icon: 'ri-shield-check-line', desc: 'Credibility & authority' },
  { id: 'stay-consistent', label: 'Stay Consistent', icon: 'ri-calendar-check-line', desc: 'Regular presence' },
] as const;

export const CONTENT_THEMES = [
  { id: 'tips', label: 'Tips' },
  { id: 'faqs', label: 'FAQs' },
  { id: 'behind-the-scenes', label: 'Behind the Scenes' },
  { id: 'storytelling', label: 'Storytelling' },
  { id: 'seasonal', label: 'Seasonal' },
  { id: 'community', label: 'Community' },
  { id: 'motivation', label: 'Motivation' },
  { id: 'soft-promotion', label: 'Soft Promotion' },
  { id: 'myths-vs-facts', label: 'Myths vs Facts' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'nostalgia', label: 'Nostalgia' },
  { id: 'trivia', label: 'Trivia' },
  { id: 'memes', label: 'Meme Energy' },
  { id: 'then-vs-now', label: 'Then vs Now' },
  { id: 'origin-story', label: 'Origin Story' },
  { id: 'funny', label: 'Funny / Relatable' },
] as const;

export const DAILY_QUANTITY_OPTIONS = [
  { id: '1' as DailyContentQuantity, label: '1 Post', icon: 'ri-file-text-line', desc: 'Quick single post' },
  { id: '3' as DailyContentQuantity, label: '3 Posts', icon: 'ri-stack-line', desc: '3 distinct angles' },
  { id: '7' as DailyContentQuantity, label: '7-Day Plan', icon: 'ri-calendar-2-line', desc: 'Full week of content' },
] as const;

export const TARGET_AUDIENCE_PRESETS = [
  { id: 'new-customers', label: 'New Customers', icon: 'ri-user-add-line' },
  { id: 'existing-customers', label: 'Existing Customers', icon: 'ri-user-heart-line' },
  { id: 'local-audience', label: 'Local Audience', icon: 'ri-map-pin-2-line' },
  { id: 'niche', label: 'Niche', icon: 'ri-focus-3-line' },
] as const;

export const SOCIAL_PLATFORMS = [
  { id: 'Facebook', label: 'Facebook', icon: 'ri-facebook-circle-line' },
  { id: 'Instagram', label: 'Instagram', icon: 'ri-instagram-line' },
  { id: 'LinkedIn', label: 'LinkedIn', icon: 'ri-linkedin-box-line' },
  { id: 'TikTok', label: 'TikTok', icon: 'ri-tiktok-line' },
  { id: 'Google Business Profile', label: 'Google Business', icon: 'ri-google-line' },
] as const;

export const POST_TYPES = [
  { id: 'Daily Promo', label: 'Daily Promo', icon: 'ri-price-tag-3-line', desc: 'A timely offer or daily push' },
  { id: 'Event Promo', label: 'Event Promo', icon: 'ri-calendar-event-line', desc: 'Drive attendance for a themed event' },
  { id: 'Product/Service Spotlight', label: 'Spotlight', icon: 'ri-lightbulb-flash-line', desc: 'Feature one product, service, or experience' },
  { id: 'Customer Engagement Question', label: 'Engagement Question', icon: 'ri-chat-smile-2-line', desc: 'Comments, opinions, and playful prompts' },
  { id: 'Behind the Scenes', label: 'Behind the Scenes', icon: 'ri-camera-lens-line', desc: 'Show personality, process, or atmosphere' },
  { id: 'Origin Story', label: 'Origin Story', icon: 'ri-book-2-line', desc: 'Tell the real story behind the identity' },
  { id: 'Meme-Inspired Post', label: 'Meme-Inspired Post', icon: 'ri-emotion-laugh-line', desc: 'Funny, culture-aware, brand-fit humor' },
  { id: 'Nostalgia Fact', label: 'Nostalgia Fact', icon: 'ri-time-line', desc: 'Remember-when content with era flavor' },
  { id: 'Then vs Now', label: 'Then vs Now', icon: 'ri-contrast-2-line', desc: 'Past versus present comparison angle' },
  { id: 'Trivia Question', label: 'Trivia Question', icon: 'ri-question-answer-line', desc: 'Interactive fact or guess-the-answer post' },
  { id: 'Funny Relatable Post', label: 'Funny Relatable', icon: 'ri-magic-line', desc: 'Share a very recognizable customer moment' },
  { id: 'Educational', label: 'Educational', icon: 'ri-book-open-line', desc: 'Tips, how-tos, or useful insights' },
  { id: 'Storytime', label: 'Storytime', icon: 'ri-chat-voice-line', desc: 'A personal mini-story with a point' },
  { id: 'Day in the Life', label: 'Day in the Life', icon: 'ri-sun-line', desc: 'A realistic slice of the creator routine' },
  { id: 'POV Post', label: 'POV Post', icon: 'ri-eye-line', desc: 'A perspective-based hook or scenario' },
  { id: 'Hot Take', label: 'Hot Take', icon: 'ri-fire-line', desc: 'A bold but defensible opinion' },
  { id: 'Relatable Post', label: 'Relatable Post', icon: 'ri-heart-3-line', desc: 'Audience sees themselves in it' },
  { id: 'Trend Remix', label: 'Trend Remix', icon: 'ri-remix-line', desc: 'A niche twist on a social trend' },
  { id: 'Audience Question', label: 'Audience Question', icon: 'ri-questionnaire-line', desc: 'Invite replies, comments, and shares' },
  { id: 'Personal Win/Loss', label: 'Personal Win/Loss', icon: 'ri-line-chart-line', desc: 'Share growth, lessons, or setbacks' },
  { id: 'Things I Wish I Knew', label: 'Wish I Knew', icon: 'ri-lightbulb-line', desc: 'Lessons for beginners or fans' },
  { id: 'Unpopular Opinion', label: 'Unpopular Opinion', icon: 'ri-megaphone-line', desc: 'A conversation-starting belief' },
] as const;

export const IDENTITY_TYPE_OPTIONS = [
  { id: 'business' as const, label: 'Business', icon: 'ri-store-2-line', desc: 'Local company, service, shop, venue' },
  { id: 'creator' as const, label: 'Creator', icon: 'ri-user-star-line', desc: 'Influencer, expert, personal brand' },
  { id: 'hybrid' as const, label: 'Hybrid', icon: 'ri-shapes-line', desc: 'Creator-led business account' },
] as const;

export const CREATOR_PERSONALITY_OPTIONS = [
  'Funny',
  'Edgy',
  'Motivational',
  'Chill',
  'Educational',
  'Relatable',
  'Bold',
  'Sarcastic',
] as const;

export const CREATOR_TONE_OPTIONS = [
  'Casual',
  'Bold',
  'Sarcastic',
  'Inspirational',
  'Unfiltered',
  'Warm',
  'High-energy',
  'Thoughtful',
] as const;

export const CONTENT_STYLE_OPTIONS = [
  { id: 'retro-90s-nostalgia', label: 'Retro 90s Nostalgia', desc: 'Remember-when hooks, era cues, playful throwback energy' },
  { id: 'funny-meme-style', label: 'Funny / Meme Style', desc: 'Internet-native, punchy, recognizable behavior' },
  { id: 'storytelling-personal', label: 'Storytelling / Personal', desc: 'Human, narrative, emotionally specific' },
  { id: 'educational-authority', label: 'Educational / Authority', desc: 'Clear lesson, credible insight, save-worthy structure' },
  { id: 'high-energy-viral', label: 'High-Energy / Viral', desc: 'Fast hook, momentum, shareable phrasing' },
  { id: 'relatable-everyday-life', label: 'Relatable / Everyday Life', desc: 'Audience recognition, everyday scenarios' },
  { id: 'controversial-hot-take', label: 'Controversial / Hot Take', desc: 'Bold opinion without cheap outrage' },
  { id: 'community-engagement', label: 'Community Engagement', desc: 'Questions, participation, local/fan connection' },
] as const;

export const CAMPAIGN_GOAL_OPTIONS = [
  { id: 'engagement', label: 'Boost Engagement', icon: 'ri-chat-3-line' },
  { id: 'bookings', label: 'Drive Bookings', icon: 'ri-calendar-check-line' },
  { id: 'foot-traffic', label: 'Increase Foot Traffic', icon: 'ri-map-pin-line' },
  { id: 'event-attendance', label: 'Promote an Event', icon: 'ri-ticket-2-line' },
  { id: 'brand-awareness', label: 'Build Awareness', icon: 'ri-megaphone-line' },
  { id: 'community', label: 'Strengthen Community', icon: 'ri-community-line' },
  { id: 'repeat-visits', label: 'Encourage Repeat Visits', icon: 'ri-repeat-line' },
  { id: 'soft-sell', label: 'Soft Sell', icon: 'ri-bard-line' },
] as const;

export const SOCIAL_POST_THEME_OPTIONS = [
  { id: 'local-business', label: 'Local business' },
  { id: 'funny', label: 'Funny' },
  { id: 'educational', label: 'Educational' },
  { id: 'community', label: 'Community' },
  { id: 'family-friendly', label: 'Family-friendly' },
  { id: 'personal-brand', label: 'Personal brand' },
  { id: 'creator-life', label: 'Creator life' },
  { id: 'customer-story', label: 'Customer story' },
  { id: 'industry-insight', label: 'Industry insight' },
  { id: 'seasonal', label: 'Seasonal' },
  { id: 'limited-time-offer', label: 'Limited-time offer' },
  { id: 'behind-the-scenes', label: 'Behind the scenes' },
  { id: 'story-driven', label: 'Story-driven' },
] as const;

export const REFERENCE_STYLE_OPTIONS = [
  { id: 'local-business', label: 'Local business', desc: 'Neighborly, real-world, trust-building' },
  { id: 'funny-relatable', label: 'Funny relatable', desc: 'Recognizable customer life moments' },
  { id: 'educational', label: 'Educational', desc: 'Teach something memorable and useful' },
  { id: 'creator-authentic', label: 'Creator authentic', desc: 'Personal, specific, and audience-aware' },
  { id: 'community-first', label: 'Community first', desc: 'Conversation, belonging, and participation' },
  { id: 'bold-opinion', label: 'Bold opinion', desc: 'Strong point of view without cheap outrage' },
  { id: 'premium-brand', label: 'Premium brand', desc: 'Clean, polished, elevated presentation' },
] as const;

export const CAPTION_LENGTHS = [
  { id: 'Short' as const, label: 'Short', desc: 'Under 100 words', icon: 'ri-text-snippet' },
  { id: 'Medium' as const, label: 'Medium', desc: '100–200 words', icon: 'ri-align-left' },
  { id: 'Long' as const, label: 'Long', desc: '200–350 words', icon: 'ri-article-line' },
];

export const BRAND_VOICES = [
  'Professional',
  'Friendly',
  'Casual',
  'Authoritative',
  'Playful',
  'Inspirational',
  'Empathetic',
  'Bold',
  'Minimalist',
  'Witty',
  'Nostalgic',
  'Community-first',
] as const;

export const SOCIAL_POST_COST = 3;
export const SOCIAL_IMAGE_COST = 5;
